import { motion } from 'framer-motion'
import { LCARSPanel, LCARSBar, Panel, GridOverlay } from '@enterpress/holomatrix'
import { 
  Cpu, 
  Database, 
  Workflow,
  Sparkles,
  Server,
  Lock,
  Bot,
  Wrench,
  Globe
} from 'lucide-react'
import { useTranslation } from '../i18n/index'

/**
 * Architecture Section - Technical Deep-Dive
 * 
 * For the technically curious — how EnterPress makes the magic happen
 * Focuses on the unified platform architecture
 */
export function Architecture() {
  const { t } = useTranslation()
  return (
    <section id="architecture" className="py-24 px-6 relative">
      {/* Holographic grid overlay for tech aesthetic */}
      <GridOverlay cellSize={60} opacity={0.02} />

      {/* Background accent */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[rgba(123,104,238,0.02)] to-transparent pointer-events-none" />
      
      <div className="max-w-6xl mx-auto relative">
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <div className="flex items-center justify-center gap-4 mb-6">
            <LCARSBar color="cyan" size="md" className="w-20" />
            <h2 className="text-sm font-bold tracking-[4px] uppercase text-[#00D4FF]">
              {t('architecture.title')}
            </h2>
            <LCARSBar color="cyan" size="md" className="w-20" />
          </div>
          <h3 className="text-4xl md:text-5xl font-bold text-white mb-4">
            {t('architecture.subtitle')}
          </h3>
          <p className="text-lg text-[rgba(232,244,248,0.6)] max-w-2xl mx-auto">
            {t('architecture.description')}
          </p>
        </motion.div>

        {/* Architecture diagram */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <LCARSPanel title={t('architecture.diagramTitle')} color="cyan">
            <div className="p-6">
              {/* Simplified architecture visualization */}
              <div className="grid md:grid-cols-3 gap-6">
                {/* Frontend */}
                <div className="text-center">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-[rgba(0,212,255,0.15)] border border-[rgba(0,212,255,0.3)] flex items-center justify-center">
                    <Sparkles size={28} className="text-[#00D4FF]" />
                  </div>
                  <div className="text-lg font-semibold text-white mb-2">{t('architecture.diagramBridge')}</div>
                  <div className="text-sm text-[rgba(232,244,248,0.5)]">
                    {t('architecture.readyRoomDesc')}
                  </div>
                  <div className="mt-3 flex flex-wrap justify-center gap-2">
                    <span className="px-2 py-1 text-xs bg-[rgba(0,212,255,0.1)] text-[#00D4FF] rounded">React</span>
                    <span className="px-2 py-1 text-xs bg-[rgba(0,212,255,0.1)] text-[#00D4FF] rounded">Real-time</span>
                  </div>
                </div>

                {/* AI Layer */}
                <div className="text-center">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-[rgba(157,140,255,0.15)] border border-[rgba(157,140,255,0.3)] flex items-center justify-center">
                    <Cpu size={28} className="text-[#9D8CFF]" />
                  </div>
                  <div className="text-lg font-semibold text-white mb-2">{t('architecture.diagramAiLayer')}</div>
                  <div className="text-sm text-[rgba(232,244,248,0.5)]">
                    {t('architecture.aiLayerDesc')}
                  </div>
                  <div className="mt-3 flex flex-wrap justify-center gap-2">
                    <span className="px-2 py-1 text-xs bg-[rgba(157,140,255,0.1)] text-[#9D8CFF] rounded">Agents</span>
                    <span className="px-2 py-1 text-xs bg-[rgba(157,140,255,0.1)] text-[#9D8CFF] rounded">MCP</span>
                    <span className="px-2 py-1 text-xs bg-[rgba(157,140,255,0.1)] text-[#9D8CFF] rounded">Memory</span>
                  </div>
                </div>

                {/* WordPress */}
                <div className="text-center">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-[rgba(255,179,71,0.15)] border border-[rgba(255,179,71,0.3)] flex items-center justify-center">
                    <Server size={28} className="text-[#FFB347]" />
                  </div>
                  <div className="text-lg font-semibold text-white mb-2">{t('architecture.diagramWpPods')}</div>
                  <div className="text-sm text-[rgba(232,244,248,0.5)]">
                    {t('architecture.wpPodsDesc')}
                  </div>
                  <div className="mt-3 flex flex-wrap justify-center gap-2">
                    <span className="px-2 py-1 text-xs bg-[rgba(255,179,71,0.1)] text-[#FFB347] rounded">K8s</span>
                    <span className="px-2 py-1 text-xs bg-[rgba(255,179,71,0.1)] text-[#FFB347] rounded">WordPress</span>
                  </div>
                </div>
              </div>

              {/* Connection lines */}
              <div className="hidden md:flex justify-center items-center gap-4 my-8">
                <div className="flex-1 h-px bg-gradient-to-r from-transparent via-[rgba(0,212,255,0.5)] to-[rgba(157,140,255,0.5)]" />
                <Workflow size={20} className="text-[rgba(232,244,248,0.4)]" />
                <div className="flex-1 h-px bg-gradient-to-r from-[rgba(157,140,255,0.5)] via-[rgba(255,179,71,0.5)] to-transparent" />
              </div>

              {/* Data flow description - hidden on mobile where it crowds the cards */}
              <div className="hidden md:block text-center text-sm text-[rgba(232,244,248,0.5)] max-w-2xl mx-auto">
                {t('architecture.dataFlow')}
              </div>
            </div>
          </LCARSPanel>
        </motion.div>

        {/* Key differentiators */}
        <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-4">
          {[
            {
              icon: Server,
              title: t('architecture.podsTitle'),
              description: t('architecture.podsDesc'),
            },
            {
              icon: Lock,
              title: t('architecture.tenancyTitle'),
              description: t('architecture.tenancyDesc'),
            },
            {
              icon: Wrench,
              title: t('architecture.toolsTitle'),
              description: t('architecture.toolsDesc'),
            },
            {
              icon: Bot,
              title: t('architecture.memoryTitle'),
              description: t('architecture.memoryDesc'),
            },
            {
              icon: Globe,
              title: t('architecture.cdnTitle'),
              description: t('architecture.cdnDesc'),
            },
          ].map((item, index) => (
            <motion.div
              key={item.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
            >
              <Panel className="p-4 h-full">
                <div className="w-10 h-10 mb-3 rounded-lg bg-[rgba(0,212,255,0.1)] flex items-center justify-center">
                  <item.icon size={20} className="text-[#00D4FF]" />
                </div>
                <div className="text-sm font-semibold text-white mb-1">
                  {item.title}
                </div>
                <div className="text-xs text-[rgba(232,244,248,0.5)] leading-relaxed">
                  {item.description}
                </div>
              </Panel>
            </motion.div>
          ))}
        </div>

        {/* Open standards callout */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="mt-12 text-center"
        >
          <Panel brackets className="inline-block px-6 py-4">
            <div className="flex items-center gap-3">
              <Database size={20} className="text-[#00D4FF]" />
              <span className="text-sm text-[rgba(232,244,248,0.7)]">
                {t('architecture.standardsCallout')}
              </span>
            </div>
          </Panel>
        </motion.div>
      </div>
    </section>
  )
}
